import React from "react";
import { styled } from "@mui/material/styles";
import { TableRow, TableCell } from "@mui/material";
import colors from "assets/theme-dark/base/colors";
import { HeadroomTableSegment } from "../../utils/headroomTableUtils";
import SortButton from "../SortButton";
import SegmentHeader from "./HeadroomSegmentHeader";
import TruncatedText from "../TruncatedText";
import { cruxyColors } from "../../theme/color-palette";

interface HeadroomTableSubHeaderRowProps {
  segments: HeadroomTableSegment[];
  sortColumn: string;
  sortDirection: "asc" | "desc";
  handleSort: (column: string) => void;
  hasOneDataAndOneManualSegment: boolean;
  isManualOnly: boolean;
  isDataOnly: boolean;
  isTwoSameMethod: boolean;
  driver: string;
}

const HeadroomTableSubHeaderRow: React.FC<HeadroomTableSubHeaderRowProps> = ({
  segments,
  sortColumn,
  sortDirection,
  handleSort,
  hasOneDataAndOneManualSegment,
  isManualOnly,
  isDataOnly,
  isTwoSameMethod,
  driver,
}) => {
  const sortedSegments = [...segments].sort((a, b) => a.segmentType.localeCompare(b.segmentType));

  const commonColumns = [
    { column: "totalCustomers", label: "# Customers" },
    { column: "totalProspects", label: "# Prospects" },
    { column: "penetration", label: "Penetration" },
    { column: "driverInitial", label: "Initial Mean" },
    { column: "driverFinal", label: "Final" },
    { column: "headroom", label: "Headroom" },
    { column: "headroomPercent", label: "Headroom (%)" },
    { column: "increasedPenetration", label: "Increased Penetration" },
    { column: "totalCustomersAfter", label: "Final # Customers" },
    { column: "totalDriverInitial", label: <TruncatedText text={`Total Initial ${driver}`} maxLength={30} /> },
    { column: "totalDriverFinal", label: <TruncatedText text={`Total Final ${driver}`} maxLength={30} /> },
    { column: "totalHeadroom", label: "Total Headroom" },
  ];

  return (
    <TableRow>
      {segments.length === 0 ? (
        <SubHeaderTableCell colSpan={2} align="center">
          No Segments Defined
        </SubHeaderTableCell>
      ) : hasOneDataAndOneManualSegment ? (
        <>
          <SubHeaderTableCell align="center">
            <SortButton
              column="range"
              label={
                <TruncatedText
                  text={`${segments.find((seg) => seg.method === "MANUAL")?.segmentType || "Manual"} (Manual)`}
                  maxLength={30}
                />
              }
              sortColumn={sortColumn}
              sortDirection={sortDirection}
              handleSort={handleSort}
            />
          </SubHeaderTableCell>
          <SubHeaderTableCell align="center">
            <SortButton
              column="segmentValue"
              label={
                <TruncatedText
                  text={`${segments.find((seg) => seg.method === "DATA")?.segmentType || "Data"} (Data)`}
                  maxLength={30}
                />
              }
              sortColumn={sortColumn}
              sortDirection={sortDirection}
              handleSort={handleSort}
            />
          </SubHeaderTableCell>
        </>
      ) : isManualOnly || isDataOnly ? (
        <>
          <SubHeaderTableCell align="center">{isManualOnly ? "Manual" : "Data"}</SubHeaderTableCell>
          <SubHeaderTableCell align="center">
            <SortButton
              column="segmentValue"
              label={
                <TruncatedText
                  text={`${segments[0].segmentType} (${isManualOnly ? "Manual" : "Data"})`}
                  maxLength={30}
                />
              }
              sortColumn={sortColumn}
              sortDirection={sortDirection}
              handleSort={handleSort}
            />
          </SubHeaderTableCell>
        </>
      ) : isTwoSameMethod ? (
        sortedSegments.map((segment, index) => (
          <SubHeaderTableCell key={`segment-header-${segment.index}`} align="center">
            <SortButton
              column={segment.method === "DATA" ? `segmentValue${index + 1}` : `range${index + 1}`}
              label={
                <TruncatedText
                  text={`${segment.segmentType} (${segment.method === "DATA" ? "Data" : "Manual"})`}
                  maxLength={30}
                />
              }
              sortColumn={sortColumn}
              sortDirection={sortDirection}
              handleSort={handleSort}
            />
          </SubHeaderTableCell>
        ))
      ) : (
        sortedSegments.map((segment, index) => (
          <SegmentHeader
            key={`segment-header-${segment.index}`}
            segment={segment}
            index={index}
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            handleSort={handleSort}
          />
        ))
      )}
      {commonColumns.map(({ column, label }) => (
        <SubHeaderTableCell key={column} align="center">
          <SortButton
            column={column}
            label={label}
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            handleSort={handleSort}
          />
        </SubHeaderTableCell>
      ))}
    </TableRow>
  );
};

const SubHeaderTableCell = styled(TableCell)(({ theme }) => ({
  border: `1px solid ${colors.grey[300]}`,
  padding: theme.spacing(1.5),
  fontSize: "0.75rem",
  backgroundColor: cruxyColors.green.main,
  color: colors.text.primary,
}));

export default HeadroomTableSubHeaderRow;
